import { useState } from 'react';
import {
  costoDeUnidadVenta,
  formatMoney,
  formatQuantity,
  lineTotal,
  margenBp,
  parseMoney,
  parseQuantity,
  type Centavos,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import type { UnidadVenta } from '@abastece/core';
import { Campo, Selector } from './Campo.js';
import { Margen } from './Margen.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { useT } from '../i18n/index.js';
import { enRango } from '../lib/costos.js';

export interface LineaEditable {
  descripcion: string;
  unidad: UnidadVenta | null;
  cantidad: Milli;
  precioLista: Centavos;
  precioUnitario: Centavos;
  motivoOverride: string | null;
  costoReferencia: UnitCost;
  /** Null for a venta rápida, which has no product and so never had a cost to know. */
  productoId?: string | null;
}

export function EditorLinea({
  linea,
  titulo,
  unidades,
  puedeEditarPrecio,
  puedeCambiarPrecio,
  verCostos,
  margenMinimoBp,
  precioDe,
  onGuardar,
  onQuitar,
  onCerrar,
}: {
  linea: LineaEditable;
  /**
   * What the sheet is headed with, when it is not the line's own description — the product's
   * name in the screen's language. The line keeps its description either way: that is what
   * the sale records and the receipt prints.
   */
  titulo?: string;
  unidades: UnidadVenta[];
  /**
   * May type a price at all (`precio_override`). A driver may not: their client's list is the
   * price, and the field shows it without taking a new one.
   */
  puedeEditarPrecio: boolean;
  /** May go below the margin floor without the owner (`precio_bajo_minimo`). */
  puedeCambiarPrecio: boolean;
  verCostos: boolean;
  margenMinimoBp: number;
  /**
   * Each presentation's price on the line's list. Given (an order), another presentation picked
   * comes at its own price, and the line with its list price; without it the price stays as typed
   * whatever is picked, as at the till.
   */
  precioDe?: (unidadVentaId: string) => Centavos | null;
  /**
   * `requiereAutorizacion`: the owner's PIN first, since whoever typed it may not allow it.
   * `delDueno`: the price is the owner's to allow, below the floor or brought down with no cost
   * known, whoever typed it. An order names the owner who allowed it, since somebody else may ring
   * its sale; the till needs no name when the owner is the one selling.
   */
  onGuardar: (cambios: Partial<LineaEditable>, requiereAutorizacion: boolean, delDueno: boolean) => void;
  onQuitar: () => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const [cantidad, setCantidad] = useState(formatQuantity(linea.cantidad));
  const [precio, setPrecio] = useState(String(linea.precioUnitario / 100));
  const [unidadId, setUnidadId] = useState(linea.unidad?.id ?? '');
  const [motivo, setMotivo] = useState(linea.motivoOverride ?? '');

  const nuevaCantidad = parseQuantity(cantidad);
  const nuevoPrecio = parseMoney(precio);
  const unidad = unidades.find((u) => u.id === unidadId) ?? linea.unidad;
  /** What the list asks for the presentation picked: the line's own, unless another was picked. */
  const precioLista =
    precioDe && unidad && unidad.id !== linea.unidad?.id ? ((precioDe(unidad.id) ?? 0) as Centavos) : linea.precioLista;

  const costoUnidad = unidad ? costoDeUnidadVenta(linea.costoReferencia, unidad.factor) : (0 as Centavos);
  const cambioDePrecio = nuevoPrecio !== null && nuevoPrecio !== precioLista;
  const margen = nuevoPrecio !== null && costoUnidad > 0 ? margenBp(nuevoPrecio, costoUnidad) : null;

  /**
   * Overriding a price is a cashier's job; selling below the margin floor is the owner's call.
   * That split is the whole of requirement 5 — negotiate freely, authorise a loss deliberately.
   */
  const bajoMinimo = margen !== null && margen < margenMinimoBp;
  /**
   * A price brought down with no cost known anywhere: nothing can say whether it still makes
   * money, so it is the owner's to allow, as a price below the floor is.
   */
  const sinCosto =
    linea.productoId != null && costoUnidad <= 0 && nuevoPrecio !== null && nuevoPrecio < precioLista;
  /**
   * A price changed asks why. Below the floor, or brought down with no cost known, it is the
   * owner's to allow: their PIN, unless whoever typed it may allow it themselves.
   */
  const delDueno = cambioDePrecio && (bajoMinimo || sinCosto);
  const requiereAutorizacion = delDueno && !puedeCambiarPrecio;
  const faltaMotivo = cambioDePrecio && !motivo.trim();
  /** Within what the books take: a code scanned into the quantity used to stop the whole till. */
  const enRangoLinea =
    nuevaCantidad !== null && nuevoPrecio !== null && enRango(() => lineTotal(nuevoPrecio, nuevaCantidad)) !== null;
  const valido = nuevaCantidad !== null && nuevaCantidad > 0 && nuevoPrecio !== null && !faltaMotivo && enRangoLinea;

  return (
    <Modal
      titulo={titulo ?? linea.descripcion}
      onCerrar={onCerrar}
      pie={
        // «Pedir autorización» is wider than half a 320 px sheet: there it goes under «Quitar».
        <div className="flex flex-wrap gap-3">
          <button type="button" className="btn-secundario flex-1" onClick={onQuitar}>
            <Ico icono="quitar" />
            {t('comun.quitar')}
          </button>
          <button
            type="button"
            className="btn-primario flex-1"
            disabled={!valido}
            onClick={() =>
              onGuardar(
                {
                  cantidad: nuevaCantidad!,
                  precioUnitario: nuevoPrecio!,
                  ...(unidad ? { unidad } : {}),
                  ...(precioDe ? { precioLista } : {}),
                  motivoOverride: cambioDePrecio ? motivo.trim() : null,
                },
                requiereAutorizacion,
                delDueno,
              )
            }
          >
            <Ico icono={requiereAutorizacion ? 'entrar' : 'guardar'} />
            {t(requiereAutorizacion ? 'linea.pedirAutorizacion' : 'comun.guardar')}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo
          etiqueta={t('comun.cantidad')}
          inputMode="decimal"
          className="tabular text-lg"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
          autoFocus
          ayuda={t('linea.ayudaCantidad')}
        />

        {unidades.length > 1 && (
          <Selector
            etiqueta={t('linea.presentacion')}
            value={unidadId}
            onChange={(e) => {
              setUnidadId(e.target.value);
              // Another presentation at its own price: the case at the case's, not at the bottle's.
              if (precioDe) {
                const suyo = e.target.value === linea.unidad?.id ? linea.precioUnitario : precioDe(e.target.value);
                setPrecio(suyo === null ? '' : String(suyo / 100));
              }
            }}
          >
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </Selector>
        )}

        <div>
          <Campo
            etiqueta={t('comun.precio')}
            inputMode="decimal"
            value={precio}
            onChange={(e) => setPrecio(e.target.value)}
            readOnly={!puedeEditarPrecio}
            className={`tabular text-lg ${puedeEditarPrecio ? '' : 'text-slate-400'}`}
            ayuda={t(puedeEditarPrecio ? 'linea.precioLista' : 'linea.precioFijo', {
              monto: formatMoney(precioLista),
            })}
          />
          {verCostos && nuevoPrecio !== null && costoUnidad > 0 && (
            <div className="mt-2 flex justify-end">
              <Margen precio={nuevoPrecio} costo={costoUnidad} minimoBp={margenMinimoBp} />
            </div>
          )}
        </div>

        {cambioDePrecio && (
          <>
            <Campo
              maxLength={200}
              etiqueta={t('linea.motivoCambio')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t('linea.ejemploMotivo')}
              autoComplete="off"
              error={faltaMotivo ? t('linea.faltaMotivo') : null}
            />
            {bajoMinimo && (
              <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                {t(puedeCambiarPrecio ? 'linea.bajoMinimoRegistrado' : 'linea.bajoMinimoPin')}
              </p>
            )}
            {sinCosto && !puedeCambiarPrecio && (
              <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">{t('linea.sinCostoPin')}</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
